// Analytics Dashboard Component
import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  Users, 
  Eye, 
  FileText, 
  TrendingUp,
  Package,
  MessageSquare,
  Mail,
  Loader2,
  RefreshCw,
  Calendar
} from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Page name mapping for display
const PAGE_NAMES = {
  '/': 'Accueil',
  '/equipment': 'Équipements',
  '/services': 'Services',
  '/partners': 'Partenaires',
  '/references': 'Références',
  '/events': 'Événements',
  '/contact': 'Contact',
  '/become-partner': 'Devenir Partenaire',
  '/client-feedback': 'Avis Clients'
};

const getPageName = (path, title) => {
  // If title is meaningful (not SAE or empty), use it
  if (title && title !== 'SAE' && title.length > 0) {
    return title;
  }
  // Use mapping
  if (PAGE_NAMES[path]) {
    return PAGE_NAMES[path];
  }
  // Dynamic routes
  if (path.startsWith('/partners/')) return 'Détail Partenaire';
  if (path.startsWith('/equipment/')) return 'Détail Équipement';
  // Fallback to path
  return path;
};

const AnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('week'); // 'week' (live+semaine), 'month', 'year'

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/analytics/summary`, {
        credentials: 'include'
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
        setError(null);
      } else {
        setError('Erreur lors du chargement des statistiques');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error}</p>
        <Button onClick={fetchAnalytics}>Réessayer</Button>
      </div>
    );
  }

  // Get values based on selected period (3 filters: week, month, year)
  const getViews = () => {
    switch (period) {
      case 'week': return data?.page_views?.week || 0;
      case 'month': return data?.page_views?.month || 0;
      case 'year': return data?.page_views?.year || 0;
      default: return data?.page_views?.week || 0;
    }
  };

  const getVisitors = () => {
    switch (period) {
      case 'week': return data?.visitors?.week || 0;
      case 'month': return data?.visitors?.month || 0;
      case 'year': return data?.visitors?.year || 0;
      default: return data?.visitors?.week || 0;
    }
  };

  const getContacts = () => {
    switch (period) {
      case 'week': return data?.forms?.contacts_week || 0;
      case 'month': return data?.forms?.contacts_month || 0;
      case 'year': return data?.forms?.contacts_total || 0;
      default: return data?.forms?.contacts_week || 0;
    }
  };

  const getFeedbacks = () => {
    switch (period) {
      case 'week': return data?.forms?.feedbacks_week || 0;
      case 'month': return data?.forms?.feedbacks_month || 0;
      case 'year': return data?.forms?.feedbacks_total || 0;
      default: return data?.forms?.feedbacks_week || 0;
    }
  };

  // 3 filters only: Live/Semaine, Mois, Année
  const periodLabels = {
    week: 'Live / Semaine',
    month: 'Ce mois',
    year: 'Cette année'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-slate-800">Statistiques</h2>
          <p className="text-slate-500">Vue d'ensemble de l'activité</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchAnalytics} disabled={loading} size="sm">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Period Filter - 3 filters only */}
      <div className="bg-white rounded-xl p-2 border border-slate-200 inline-flex gap-1">
        {['week', 'month', 'year'].map((p) => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              period === p 
                ? 'bg-slate-900 text-white' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {periodLabels[p]}
          </button>
        ))}
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Views */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between mb-3">
            <Eye className="w-8 h-8 opacity-80" />
            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">{periodLabels[period]}</span>
          </div>
          <p className="text-4xl font-bold">{getViews().toLocaleString()}</p>
          <p className="text-blue-100 text-sm mt-1">Pages vues</p>
        </div>

        {/* Visitors */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between mb-3">
            <Users className="w-8 h-8 opacity-80" />
            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">{periodLabels[period]}</span>
          </div>
          <p className="text-4xl font-bold">{getVisitors().toLocaleString()}</p>
          <p className="text-green-100 text-sm mt-1">Visiteurs uniques</p>
        </div>

        {/* Messages */}
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between mb-3">
            <Mail className="w-8 h-8 opacity-80" />
            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">{periodLabels[period]}</span>
          </div>
          <p className="text-4xl font-bold">{getContacts().toLocaleString()}</p>
          <p className="text-purple-100 text-sm mt-1">Messages contact</p>
        </div>

        {/* Feedbacks */}
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between mb-3">
            <MessageSquare className="w-8 h-8 opacity-80" />
            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">{periodLabels[period]}</span>
          </div>
          <p className="text-4xl font-bold">{getFeedbacks().toLocaleString()}</p>
          <p className="text-orange-100 text-sm mt-1">Demandes partenariat</p>
        </div>
      </div>

      {/* Totals Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <p className="text-sm text-slate-500">Total vues</p>
          <p className="text-2xl font-bold text-slate-800">{(data?.page_views?.total || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <p className="text-sm text-slate-500">Total visiteurs (année)</p>
          <p className="text-2xl font-bold text-slate-800">{(data?.visitors?.year || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <p className="text-sm text-slate-500">Total messages</p>
          <p className="text-2xl font-bold text-slate-800">{(data?.forms?.contacts_total || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <p className="text-sm text-slate-500">Total demandes</p>
          <p className="text-2xl font-bold text-slate-800">{(data?.forms?.feedbacks_total || 0).toLocaleString()}</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Views Chart */}
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <h3 className="font-medium text-slate-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-500" />
            Vues des 7 derniers jours
          </h3>
          <div className="flex items-end justify-between h-40 gap-2">
            {data?.daily_views?.map((day, index) => {
              const maxViews = Math.max(...(data.daily_views?.map(d => d.views) || [1]), 1);
              const height = (day.views / maxViews) * 100;
              const dayName = new Date(day.date).toLocaleDateString('fr-FR', { weekday: 'short' });
              return (
                <div key={index} className="flex flex-col items-center flex-1">
                  <span className="text-xs text-slate-600 font-medium mb-1">{day.views}</span>
                  <div 
                    className="w-full bg-blue-500 rounded-t-md transition-all hover:bg-blue-600"
                    style={{ height: `${Math.max(height, 8)}%` }}
                    title={`${day.date}: ${day.views} vues`}
                  />
                  <span className="text-xs text-slate-400 mt-2">{dayName}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Pages */}
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <h3 className="font-medium text-slate-800 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-500" />
            Pages les plus visitées (7 jours)
          </h3>
          <div className="space-y-3">
            {data?.top_pages?.length > 0 ? (
              data.top_pages.slice(0, 5).map((page, index) => {
                const pageName = getPageName(page.path, page.title);
                const maxViews = Math.max(...(data.top_pages?.map(p => p.views) || [1]));
                const barWidth = (page.views / maxViews) * 100;
                return (
                  <div key={index} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 text-xs flex items-center justify-center font-bold">
                          {index + 1}
                        </span>
                        <span className="text-sm font-medium text-slate-700">{pageName}</span>
                      </div>
                      <span className="text-sm font-bold text-slate-600">{page.views}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-purple-500 rounded-full transition-all"
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-slate-400 text-center py-8">Aucune donnée disponible</p>
            )}
          </div>
        </div>
      </div>

      {/* Equipment Stats */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="font-medium text-slate-800 mb-4 flex items-center gap-2">
          <Package className="w-5 h-5 text-green-500" />
          Équipements les plus consultés
        </h3>
        {data?.top_equipment?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.top_equipment.slice(0, 6).map((eq, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center font-bold text-green-600">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">
                    {eq.equipment_name || `Équipement`}
                  </p>
                  <p className="text-xs text-slate-500">{eq.views} consultations</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Aucune consultation d'équipement enregistrée</p>
            <p className="text-xs mt-1">Les consultations seront trackées automatiquement</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
